/**
 * What a study that screens nobody needs in place of a screener. Everyone who
 * matches its filters starts the task, and a `completed` redirect or a self
 * report comes from the participant's own browser, so a person has to approve
 * the work before it is paid, and applicants cannot be held for a review that
 * only a screening step reaches.
 *
 * Shared by the launch gate and the dashboard, which shows the same answer
 * before the launch is attempted.
 */

import {
  CustomerScreeningReview,
  isProjectBasedPayRate,
  mayCompletionSignalApprove,
  type ReviewType,
  type StudyPayRateFrequency,
  SubmissionTaskType,
} from "@repo/db/constants";

/**
 * Tasks that confirm a step instead of delivering the work a study pays for. A
 * self-approving one cannot release a payout on its own: a one-time study pays
 * only once every task is approved.
 */
const CONFIRMATION_TASK_TYPES = new Set<string>([
  SubmissionTaskType.AGREEMENT,
  SubmissionTaskType.E_SIGNATURE,
  SubmissionTaskType.JOIN_SLACK,
  SubmissionTaskType.SLACK_HANDOFF,
  SubmissionTaskType.INSTALL_TIME_TRACKER,
]);

export interface UnscreenedReviewTask {
  sequence: number;
  taskType: string;
  reviewType: ReviewType;
}

/** Whether a task confirms a step, which may self-report on any study. */
export function isConfirmationTask(task: { taskType: string }): boolean {
  return CONFIRMATION_TASK_TYPES.has(task.taskType);
}

/** Whether a task's own completion signal would approve the work it delivers. */
export function isSelfApprovingWork(task: UnscreenedReviewTask): boolean {
  return (
    !isConfirmationTask(task) && mayCompletionSignalApprove(task.reviewType)
  );
}

export type UnscreenedTaskReview<T extends UnscreenedReviewTask> =
  | { kind: "SELF_APPROVING_WORK"; tasks: T[] }
  | { kind: "NO_REVIEWED_TASK" };

/**
 * Why a study that screens nobody cannot run these tasks, or null when it can.
 * An hourly or per-task study is exempt: its approved tasks never approve the
 * submission, and its payouts run on a separate batch.
 */
export function unscreenedTaskReview<T extends UnscreenedReviewTask>(study: {
  taskPlan: T[];
  payRateFrequency: StudyPayRateFrequency | null | undefined;
}): UnscreenedTaskReview<T> | null {
  if (isProjectBasedPayRate(study.payRateFrequency)) {
    return null;
  }
  const selfApprovingWork = study.taskPlan.filter(isSelfApprovingWork);
  if (selfApprovingWork.length > 0) {
    return { kind: "SELF_APPROVING_WORK", tasks: selfApprovingWork };
  }
  if (
    !study.taskPlan.some((task) => !mayCompletionSignalApprove(task.reviewType))
  ) {
    return { kind: "NO_REVIEWED_TASK" };
  }
  return null;
}

/**
 * Whether a study that screens nobody holds applicants for a review it never
 * reaches: that review comes after a screening step, so they would start the
 * task unreviewed.
 */
export function holdsUnscreenedApplicantsForReview(
  customerScreeningReview: CustomerScreeningReview | null | undefined,
): boolean {
  return customerScreeningReview === CustomerScreeningReview.MANUAL_REVIEW;
}

/**
 * Whether a screening guide carries any questions, read from stored JSONB that
 * may be malformed. The same reading as the server's `screeningGuideHasQuestions`.
 */
export function screeningGuideHasAnyQuestion(guide: unknown): boolean {
  const questions = (guide as { questions?: unknown } | null | undefined)
    ?.questions;
  return Array.isArray(questions) && questions.length > 0;
}
